/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { GanttRowLabelHover } from '../Gantt.types';
import { GanttRowLayoutObj } from '../utils/dataLayoutUtils';

type UseRowLabelPointerGestureOptions<K1, K2> = {
  rowObjs: GanttRowLayoutObj<K1, K2>[];
  hoverProps: GanttRowLabelHover<K1>;
};

/**
 * Hook that handles row labels pointer gestures.
 */
const useGanttRowLabelPointerGestures = <K1, K2>({
  rowObjs,
  hoverProps
}: UseRowLabelPointerGestureOptions<K1, K2>) => {
  const idExtracter = (event: PointerEvent) => {
    const { clientX: x, clientY: y } = event;
    // Just for fun, an alternative to getInfo -> getElementData that Chart is doing.
    // TODO: think about whether this is more useful if we were to have a
    // DVT shared "points to elements" system. At face value, this actually might be slightly less
    // efficient than the Chart method right now *in general*, because elementsFromPoint gives us ancestors
    // beyond the root. For our row labels purpose however, there's no "non-rowlabel" whitespace
    // so we'll definitely hit the element we want in like 2ish iterations so poor performance is not a concern.
    const elements = document.elementsFromPoint(x, y) as HTMLElement[] | SVGElement[];
    for (let i = 0; i < elements.length; i++) {
      const dataRowIndex = elements[i].dataset['ojRowIndex'];
      if (dataRowIndex) {
        return rowObjs[Number(dataRowIndex)].id;
      }
    }
    return undefined;
  };

  const onPointerMove = (event: PointerEvent) => {
    const id = idExtracter(event);
    if (id !== hoverProps.hoveredRowLabelId) {
      hoverProps.onRowLabelHoverChange({ id });
    }
  };

  return { onPointerMove };
};

export { useGanttRowLabelPointerGestures };
